/** What every page shares above its layout. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Outlet, ScrollRestoration, type Location } from 'react-router-dom'
import { Modal, useToast } from '@metal/ui'
import { onSessionExpired } from '@metal/ui/api'
import { useSession } from '@metal/ui/session'
import { AuthPanel } from '@/screens/onboarding/AuthPanel'

interface AuthModalValue {
  /** Opens sign in or sign up over the current page. */
  open: () => void
  close: () => void
}

const AuthModalContext = createContext<AuthModalValue | null>(null)

export function useAuthModal(): AuthModalValue {
  const ctx = useContext(AuthModalContext)
  if (!ctx) throw new Error('useAuthModal must be used under the app root')
  return ctx
}

function AuthModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [generation, setGeneration] = useState(0)

  const value = useMemo<AuthModalValue>(
    () => ({
      open: () => {
        setGeneration((g) => g + 1)
        setOpen(true)
      },
      close: () => setOpen(false),
    }),
    [],
  )

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <Modal open={open} onClose={value.close} title="ورود یا ثبت‌نام" width={420}>
        <AuthPanel key={generation} onDone={value.close} />
      </Modal>
    </AuthModalContext.Provider>
  )
}

/** A session that could not be renewed says so once, instead of failing quietly. */
function SessionExpiryNotice() {
  const toast = useToast()
  const push = useCallback(
    () => toast.push({ icon: 'alert', message: 'نشست شما به پایان رسید. دوباره وارد شوید.' }),
    [toast],
  )
  useEffect(() => onSessionExpired(push), [push])
  return null
}

const NOT_MEMBER = 'حساب مدیریت از پنل اپراتور وارد می‌شود.'

/** Staff work in the console, under the console's own refresh cookie. */
function StaffSessionRefusal() {
  const { user, signOut } = useSession()
  const toast = useToast()
  const staff = Boolean(user?.operator)
  useEffect(() => {
    if (!staff) return
    void signOut()
    toast.push({ icon: 'alert', message: NOT_MEMBER })
  }, [staff, signOut, toast])
  return null
}

/** The window is the page's scroll. */
const byPath = (location: Location) => location.pathname

export function AppRoot() {
  return (
    <AuthModalProvider>
      <SessionExpiryNotice />
      <StaffSessionRefusal />
      <ScrollRestoration getKey={byPath} />
      <Outlet />
    </AuthModalProvider>
  )
}
